import { CanvasTextMetrics, TextStyle, nextPow2 } from 'pixi.js';

// Pixi rounds text canvases up to a power of two, so text wider than 16384
// device pixels gets a 32768px canvas, one pixel past Firefox's limit.
const MAX_CANVAS_SIDE = 16384;
// iOS Safari refuses canvases larger than 4096 * 4096 pixels.
const MAX_CANVAS_AREA = 4096 * 4096;

const fitsCanvas = (width, height, resolution, maxSide) => {
	const canvasWidth = nextPow2(Math.ceil(width * resolution));
	const canvasHeight = nextPow2(Math.ceil(height * resolution));
	return canvasWidth <= maxSide && canvasHeight <= maxSide && canvasWidth * canvasHeight <= MAX_CANVAS_AREA;
};

/**
 * Highest resolution, up to the requested one, at which a text canvas of
 * width x height (unscaled, padding included) stays within the size limits.
 */
export const getSafeTextResolution = (width, height, resolution, maxSide = MAX_CANVAS_SIDE) => {
	if (fitsCanvas(width, height, resolution, maxSide)) return resolution;

	let safeResolution = 0;
	for (let canvasWidth = 1; canvasWidth <= maxSide; canvasWidth *= 2) {
		const canvasHeight = Math.min(maxSide, MAX_CANVAS_AREA / canvasWidth);
		safeResolution = Math.max(safeResolution, Math.min(canvasWidth / width, canvasHeight / height));
	}
	// stay just under the power of two so float rounding cannot push Pixi's ceil past it
	return safeResolution * 0.9999;
};

export const limitTextResolution = renderer => {
	const textSystem = renderer.canvasText;
	const getTexture = textSystem.getTexture.bind(textSystem);
	const gl = renderer.gl;
	const maxSide = Math.min(MAX_CANVAS_SIDE, gl?.getParameter(gl.MAX_TEXTURE_SIZE) || MAX_CANVAS_SIDE);

	textSystem.getTexture = (options, ...args) => {
		if (typeof options === 'string') return getTexture(options, ...args);

		if (!(options.style instanceof TextStyle)) options.style = new TextStyle(options.style);
		const { style } = options;
		const padding = style._getFinalPadding();
		const measured = CanvasTextMetrics.measureText(`${options.text}` || ' ', style);
		const width = Math.ceil(Math.max(1, measured.width) + padding * 2);
		const height = Math.ceil(Math.max(1, measured.height) + padding * 2);
		options.resolution = getSafeTextResolution(width, height, options.resolution ?? renderer.resolution, maxSide);

		return getTexture(options);
	};
};
